import { useQuery } from '@tanstack/react-query';
import { parseEventSearchParams, toQueryParams } from 'components/VenueSearch/SearchUtils';
import { GET_EVENT } from 'constants/queryKeys';
import {
    EventDetails,
    eventDetailsQueryKey,
    eventDetailsQueryOptions,
    fetchEventDetails,
} from 'hooks/queries/useEventDetailsQuery';
import { queryClient } from 'lib/queryClient';
import pick from 'lodash/pick';
import React from 'react';
import { useParams } from 'react-router-dom';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { deselectVenue, loadSuggestedVenuesForEvent, selectVenue, updateEvent, updateEventNoDebounce } from '../../api';
import {
    addCollaborators as addCollaboratorsAPI,
    isAddCollaboratorsPartialError,
    removeCollaborator as removeCollaboratorApi,
    updateCollaboratorPermission as updateCollaboratorPermissionApi,
} from '../../api/collaborators';
import { mergeDateTime, moveDayBy, timeValue } from '../../utils/date_util';

/*
 * The event, its template and its collaborators are server state: they live in the TanStack Query
 * cache under eventDetailsQueryKey(eventId), so any invalidation of [GET_EVENT, eventId] elsewhere
 * refreshes them. The actions below keep their optimistic-update / debounced-save behavior but
 * read and write that cache. Zustand holds only what is not the event itself: the active event id,
 * suggested venues and their search params, and the provider reload key.
 */

const initialEvent: Bizly.Event = {
    id: 0,
    name: '',
    schedule: [],
    description: '',
    objective: '',
    attendeeCounts: {
        total: 0,
        attending: 0,
        notAttending: 0,
        invited: 0,
        notSent: 0,
    },
    editable: false,
    plannedBy: { firstName: '', lastName: '', imageUrl: '', email: '' },
    currency: { id: -1, code: 'USD' },
    acceptedProposalCurrency: null,
    requirements: [],
    collaborators: [],
    status: { id: 0, code: 'new', rank: 0 },
    estAttendance: 0,
};

const PENDING_ADD_COLLABORATOR_ID = -1;
export const collaboratorIsPendingAdd = (collaborator: Pick<BizlyAPI.EventCollaboratorOrPending, 'id'>) =>
    collaborator.id === PENDING_ADD_COLLABORATOR_ID;

type VenueSearchParams = BizlyAPI.VenueFacets & Partial<{ q: string; place_id: string; lat: number; lng: number }>;

// Stable fallback references — must be module-level constants so selectors don't return a new
// reference on every call (which would cause infinite re-renders for subscribed components).
const INITIAL_TEMPLATE = { id: 0 } as Bizly.EventTemplate;
const EMPTY_COLLABORATORS: BizlyAPI.EventCollaboratorOrPending[] = [];
const EMPTY_VENUES: Bizly.Venue[] = [];

const detailsOf = (eventId: number) => queryClient.getQueryData<EventDetails>(eventDetailsQueryKey(eventId));
const eventOf = (eventId: number) => detailsOf(eventId)?.event ?? initialEvent;
const collaboratorsOf = (eventId: number) => detailsOf(eventId)?.collaborators ?? EMPTY_COLLABORATORS;

const writeDetails = (eventId: number, patch: Partial<EventDetails>) =>
    queryClient.setQueryData<EventDetails>(eventDetailsQueryKey(eventId), details =>
        details ? { ...details, ...patch } : details
    );
const writeEvent = (eventId: number, event: Bizly.Event) => writeDetails(eventId, { event });
const writeCollaborators = (eventId: number, collaborators: BizlyAPI.EventCollaboratorOrPending[]) =>
    writeDetails(eventId, { collaborators });

// Stops an in-flight refetch from landing on top of an optimistic write with older server data.
const cancelEventRefetch = (eventId: number) => queryClient.cancelQueries({ queryKey: eventDetailsQueryKey(eventId) });

type EventUiState = {
    suggestedVenues: Array<Bizly.Venue>;
    venueSearchParams?: VenueSearchParams;
    loadedEventId: number | null;
    suggestionsLoadedEventId: number;
    key: number; // For provider reloading
};

type EventStore = {
    // UI state per event ID
    events: Record<number, EventUiState>;

    // Current active event ID
    activeEventId: number | null;

    // Actions
    setActiveEvent: (eventId: number) => void;
    loadEventData: (eventId: number, isEnterprise: boolean, showTestingVenues?: boolean) => Promise<void>;
    onEventChange: (eventId: number, newEvent: Partial<Bizly.Event>) => Promise<void>;
    refreshEvent: (eventId: number) => Promise<void>;
    onDescriptionChange: (eventId: number, newValue: string) => Promise<void>;
    onObjectiveChange: (eventId: number, newValue: string) => Promise<void>;
    onVenueSelect: (eventId: number, venue: Bizly.Venue) => Promise<void>;
    onLocationChange: (eventId: number, location: string, googlePlaceId: string) => Promise<void>;
    onAddDay: (eventId: number, options: { append?: boolean }) => Promise<void>;
    onRemoveDay: (eventId: number, dayIndex: number) => Promise<void>;
    onDayChange: (eventId: number, dayIndex: number, newValue: Bizly.Day) => Promise<void>;
    updateSchedule: (eventId: number, schedule: Bizly.Day[]) => Promise<void>;
    updateAttendeesCount: (eventId: number, attendeeCounts: Bizly.AttendeeCounts) => void;
    setEvent: (eventId: number, event: Bizly.Event) => void;
    setAcceptedProposalCurrency: (eventId: number, currency?: Bizly.Currency | null) => void;
    reloadProvider: (eventId: number) => void;
    setCollaborators: (eventId: number, collaborators: BizlyAPI.EventCollaboratorOrPending[]) => void;

    // Collaborator actions
    removeCollaborator: (eventId: number, collaboratorId: number) => Promise<void>;
    updateCollaboratorPermission: (eventId: number, collaboratorId: number, edit: boolean) => Promise<void>;
    addCollaborators: (eventId: number, newCollaborators: BizlyAPI.NewEventCollaborator[]) => Promise<void>;
    isEditor: (eventId: number, user: Bizly.User) => boolean;
    canEditEvent: (eventId: number) => boolean;

    // Internal helpers
    fetchSuggestedVenues: (eventId: number, venueSearchParams: VenueSearchParams) => Promise<void>;
};

const createInitialEventUiState = (): EventUiState => ({
    suggestedVenues: EMPTY_VENUES,
    venueSearchParams: undefined,
    loadedEventId: null,
    suggestionsLoadedEventId: -1,
    key: 0,
});

const useEventStore = create<EventStore>()(
    subscribeWithSelector((set, get) => {
        const setUi = (eventId: number, patch: Partial<EventUiState>) =>
            set(state => ({
                events: {
                    ...state.events,
                    [eventId]: { ...(state.events[eventId] ?? createInitialEventUiState()), ...patch },
                },
            }));

        // Optimistically writes the event, saves it, then (optionally) adopts the server's copy.
        // Reverts the optimistic write if the save fails.
        const saveOptimistically = async (
            eventId: number,
            buildEvent: (event: Bizly.Event) => Bizly.Event,
            save: (updated: Bizly.Event, previous: Bizly.Event) => Promise<Bizly.Event | void>
        ) => {
            await cancelEventRefetch(eventId);
            const previous = eventOf(eventId);
            const updated = buildEvent(previous);
            writeEvent(eventId, updated);

            try {
                const fromServer = await save(updated, previous);
                if (fromServer) writeEvent(eventId, fromServer);
            } catch (e) {
                writeEvent(eventId, previous);
                throw e;
            }
        };

        return {
            events: {},
            activeEventId: null,

            setActiveEvent: (eventId: number) => {
                set({ activeEventId: eventId });
            },

            loadEventData: async (eventId: number, isEnterprise: boolean, showTestingVenues = false) => {
                if (get().events[eventId]?.loadedEventId === eventId) {
                    return; // Already loaded
                }

                try {
                    const { event } = await queryClient.ensureQueryData({
                        queryKey: eventDetailsQueryKey(eventId),
                        queryFn: () => fetchEventDetails(eventId),
                    });

                    const venueSearchParams = toQueryParams({
                        ...parseEventSearchParams(event, isEnterprise, showTestingVenues),
                        ...(event.lat && event.lng
                            ? {
                                  lat: event.lat,
                                  lng: event.lng,
                              }
                            : {}),
                    }) as VenueSearchParams;

                    setUi(eventId, { loadedEventId: eventId, venueSearchParams });

                    // Load suggested venues
                    await get().fetchSuggestedVenues(eventId, venueSearchParams);
                } catch (e) {
                    console.error(`Could not fetch data for meeting ${eventId}`, e);
                }
            },

            fetchSuggestedVenues: async (eventId: number, venueSearchParams: VenueSearchParams) => {
                if (get().events[eventId]?.suggestionsLoadedEventId === eventId) {
                    return; // Already loaded
                }

                try {
                    const suggestedVenues = await loadSuggestedVenuesForEvent(eventId, venueSearchParams);
                    setUi(eventId, { suggestedVenues, suggestionsLoadedEventId: eventId });
                } catch (e) {
                    console.error(`Could not fetch suggested venues for event ${eventId}`, e);
                }
            },

            // Not optimistic: the change is written only once the save succeeds.
            onEventChange: async (eventId: number, newEvent: Partial<Bizly.Event>) => {
                await updateEvent({ id: eventId, ...newEvent });
                await cancelEventRefetch(eventId);
                writeEvent(eventId, { ...eventOf(eventId), ...newEvent });
            },

            refreshEvent: async (eventId: number) => {
                try {
                    await queryClient.invalidateQueries({ queryKey: [GET_EVENT, eventId] });
                } catch (e) {
                    console.error(`Could not refresh event ${eventId}`, e);
                }
            },

            onDescriptionChange: (eventId: number, newValue: string) =>
                saveOptimistically(
                    eventId,
                    event => ({ ...event, description: newValue }),
                    async updated => {
                        await updateEvent(updated);
                    }
                ),

            onObjectiveChange: (eventId: number, newValue: string) =>
                saveOptimistically(
                    eventId,
                    event => ({ ...event, objective: newValue }),
                    async updated => {
                        await updateEvent(updated);
                    }
                ),

            onVenueSelect: (eventId: number, venue: Bizly.Venue) =>
                saveOptimistically(
                    eventId,
                    event => {
                        const existingSelections = event.selectedVenues || [];
                        const isAdding = !existingSelections.find((v: Bizly.Venue) => v.id === venue.id);
                        return {
                            ...event,
                            selectedVenues: isAdding
                                ? existingSelections.concat(venue)
                                : existingSelections.filter((v: Bizly.Venue) => v.id !== venue.id),
                        };
                    },
                    async (_updated, previous) => {
                        const isAdding = !(previous.selectedVenues || []).find((v: Bizly.Venue) => v.id === venue.id);
                        await (isAdding ? selectVenue(previous, venue) : deselectVenue(previous, venue));
                    }
                ),

            onLocationChange: (eventId: number, location: string, googlePlaceId: string) =>
                saveOptimistically(
                    eventId,
                    event => ({ ...event, location, googlePlaceId }),
                    async (_updated, previous) => {
                        const updatedEvent = await updateEventNoDebounce({
                            ...previous,
                            id: previous.id,
                            location,
                            googlePlaceId,
                        });
                        return {
                            ...eventOf(eventId),
                            ...pick(updatedEvent, ['location', 'googlePlaceId', 'lat', 'lng']),
                        };
                    }
                ),

            onAddDay: (eventId: number, { append = true }) =>
                saveOptimistically(
                    eventId,
                    event => {
                        const delta = append ? 1 : -1;
                        const clonedArray = event.schedule.slice();
                        const { startDate } = clonedArray[append ? clonedArray.length - 1 : 0];

                        const newDay: Bizly.Day = {
                            start: null,
                            end: null,
                            startDate: moveDayBy(startDate, delta),
                            startTime: null,
                            endTime: null,
                            agenda: [],
                            guestrooms: [],
                            addToInquiry: true,
                        };

                        return {
                            ...event,
                            schedule: append ? clonedArray.concat(newDay) : [newDay].concat(clonedArray),
                        };
                    },
                    updated => updateEvent(updated)
                ),

            onRemoveDay: (eventId: number, dayIndex: number) =>
                saveOptimistically(
                    eventId,
                    event => ({ ...event, schedule: event.schedule.filter((_, index) => index !== dayIndex) }),
                    updated => updateEvent(updated)
                ),

            onDayChange: (eventId: number, dayIndex: number, newValue: Bizly.Day) =>
                saveOptimistically(
                    eventId,
                    event => {
                        const { schedule: currentSchedule, startsAt: currentStart, endsAt: currentEnd } = event;
                        const { start: currentDayStart, end: currentDayEnd } = currentSchedule[dayIndex];
                        const { startDate, startTime, endTime } = newValue;

                        const endDate =
                            startDate && startTime && endTime && timeValue(startTime) > timeValue(endTime)
                                ? moveDayBy(startDate)
                                : startDate;
                        const newDayStart = startDate && startTime && mergeDateTime(startDate, startTime);
                        const newDayEnd = endDate && endTime && mergeDateTime(endDate, endTime);

                        const updatedDay = {
                            ...newValue,
                            start: newDayStart || currentDayStart,
                            end: newDayEnd || currentDayEnd,
                            agenda: newValue.agenda.map(entry => {
                                const { startTime: agStartTime, endTime: agEndTime, start, end } = entry;
                                return {
                                    ...entry,
                                    start: (startDate && agStartTime && mergeDateTime(startDate, agStartTime)) || start,
                                    end: (endDate && agEndTime && mergeDateTime(endDate, agEndTime)) || end,
                                };
                            }),
                        };

                        const isFirstDay = dayIndex === 0;
                        const isLastDay = dayIndex === currentSchedule.length - 1;

                        return {
                            ...event,
                            startsAt: isFirstDay && newDayStart ? newDayStart : currentStart,
                            endsAt: isLastDay && newDayEnd ? newDayEnd : currentEnd,
                            schedule: currentSchedule.map((day, index) => (index === dayIndex ? updatedDay : day)),
                        };
                    },
                    updated => updateEvent(updated)
                ),

            updateSchedule: (eventId: number, schedule: Bizly.Day[]) =>
                saveOptimistically(
                    eventId,
                    event => ({ ...event, schedule }),
                    updated => updateEvent(updated)
                ),

            updateAttendeesCount: (eventId: number, attendeeCounts: Bizly.AttendeeCounts) => {
                writeEvent(eventId, { ...eventOf(eventId), attendeeCounts });
            },

            setEvent: (eventId: number, event: Bizly.Event) => {
                writeEvent(eventId, event);
            },

            setAcceptedProposalCurrency: (eventId: number, currency?: Bizly.Currency | null) => {
                writeEvent(eventId, {
                    ...eventOf(eventId),
                    acceptedProposalCurrency: currency ?? { id: -1, code: 'USD' },
                });
            },

            reloadProvider: (eventId: number) => {
                setUi(eventId, { key: (get().events[eventId]?.key ?? 0) + 1 });
            },

            setCollaborators: (eventId: number, collaborators: BizlyAPI.EventCollaboratorOrPending[]) => {
                writeCollaborators(eventId, collaborators);
            },

            removeCollaborator: async (eventId: number, collaboratorId: number) => {
                if (collaboratorId === PENDING_ADD_COLLABORATOR_ID) return;

                await cancelEventRefetch(eventId);
                const collaborators = collaboratorsOf(eventId);
                const collaboratorToRemove = collaborators.find(collaborator => collaborator.id === collaboratorId);

                // Optimistic update
                writeCollaborators(
                    eventId,
                    collaborators.filter(collaborator => collaboratorId !== collaborator.id)
                );

                try {
                    await removeCollaboratorApi(eventId, collaboratorId);
                } catch (error) {
                    // Revert on error
                    if (collaboratorToRemove) {
                        writeCollaborators(eventId, [...collaboratorsOf(eventId), collaboratorToRemove]);
                    }
                    throw error;
                }
            },

            updateCollaboratorPermission: async (eventId: number, collaboratorId: number, edit: boolean) => {
                await cancelEventRefetch(eventId);
                const collaborators = collaboratorsOf(eventId);

                // Optimistic update
                writeCollaborators(
                    eventId,
                    collaborators.map(c => (c.id === collaboratorId ? { ...c, editor: edit } : c))
                );

                try {
                    await updateCollaboratorPermissionApi(eventId, collaboratorId, edit);
                } catch (error) {
                    // Revert on error
                    writeCollaborators(eventId, collaborators);
                    throw error;
                }
            },

            addCollaborators: async (eventId: number, newCollaborators: BizlyAPI.NewEventCollaborator[]) => {
                const currentEmails = new Set(collaboratorsOf(eventId).map(c => c.email));
                const newAdditions = newCollaborators.filter(c => !currentEmails.has(c.email));

                const result = await addCollaboratorsAPI(newAdditions, eventId);
                writeCollaborators(eventId, result.collaborators);
            },

            isEditor: (eventId: number, user: Bizly.User) => {
                const event = eventOf(eventId);
                // Event owner always has edit access
                if (event.plannedBy?.email === user.email) return true;
                const isCollaborator = collaboratorsOf(eventId).find(collaborator => collaborator.userId === user.id);
                return (isCollaborator && isCollaborator.editor) ?? false;
            },

            canEditEvent: (eventId: number) => {
                return eventOf(eventId).editable;
                // TODO: Uncomment this when we have a way to check event team
                // if (!event.editable) return false;
                // return get().isEditor(eventId, user);
            },
        };
    })
);

// Export actions for external use
export const eventActions = {
    setActiveEvent: (eventId: number) => useEventStore.getState().setActiveEvent(eventId),
    loadEventData: (eventId: number, isEnterprise: boolean, showTestingVenues?: boolean) =>
        useEventStore.getState().loadEventData(eventId, isEnterprise, showTestingVenues),
    onEventChange: async (eventId: number, newEvent: Partial<Bizly.Event>) =>
        await useEventStore.getState().onEventChange(eventId, newEvent),
    refreshEvent: (eventId: number) => useEventStore.getState().refreshEvent(eventId),
    onDescriptionChange: (eventId: number, newValue: string) =>
        useEventStore.getState().onDescriptionChange(eventId, newValue),
    onObjectiveChange: (eventId: number, newValue: string) =>
        useEventStore.getState().onObjectiveChange(eventId, newValue),
    onVenueSelect: (eventId: number, venue: Bizly.Venue) => useEventStore.getState().onVenueSelect(eventId, venue),
    onLocationChange: (eventId: number, location: string, googlePlaceId: string) =>
        useEventStore.getState().onLocationChange(eventId, location, googlePlaceId),
    onAddDay: (eventId: number, options: { append?: boolean }) => useEventStore.getState().onAddDay(eventId, options),
    onRemoveDay: (eventId: number, dayIndex: number) => useEventStore.getState().onRemoveDay(eventId, dayIndex),
    onDayChange: (eventId: number, dayIndex: number, newValue: Bizly.Day) =>
        useEventStore.getState().onDayChange(eventId, dayIndex, newValue),
    updateSchedule: (eventId: number, schedule: Bizly.Day[]) =>
        useEventStore.getState().updateSchedule(eventId, schedule),
    updateAttendeesCount: (eventId: number, attendeeCounts: Bizly.AttendeeCounts) =>
        useEventStore.getState().updateAttendeesCount(eventId, attendeeCounts),
    setEvent: (eventId: number, event: Bizly.Event) => useEventStore.getState().setEvent(eventId, event),
    setAcceptedProposalCurrency: (eventId: number, currency?: Bizly.Currency | null) =>
        useEventStore.getState().setAcceptedProposalCurrency(eventId, currency),
    reloadProvider: (eventId: number) => useEventStore.getState().reloadProvider(eventId),
    setCollaborators: (eventId: number, collaborators: BizlyAPI.EventCollaboratorOrPending[]) =>
        useEventStore.getState().setCollaborators(eventId, collaborators),
    removeCollaborator: (eventId: number, collaboratorId: number) =>
        useEventStore.getState().removeCollaborator(eventId, collaboratorId),
    updateCollaboratorPermission: (eventId: number, collaboratorId: number, edit: boolean) =>
        useEventStore.getState().updateCollaboratorPermission(eventId, collaboratorId, edit),
    addCollaborators: (eventId: number, newCollaborators: BizlyAPI.NewEventCollaborator[]) =>
        useEventStore.getState().addCollaborators(eventId, newCollaborators),
    isEditor: (eventId: number, user: Bizly.User) => useEventStore.getState().isEditor(eventId, user),
    canEditEvent: (eventId: number) => useEventStore.getState().canEditEvent(eventId),
};

// Reads the event details from the cache without fetching them. Fetching (and keeping the query
// active, so invalidations refetch it) is owned by useEventManager, mounted by EventStoreInitializer
// on the event pages. That keeps a component that merely renders under an :eventId route from
// issuing an authenticated request on a page that never loaded the event.
const useCachedEventDetails = (eventId: number) => useQuery({ ...eventDetailsQueryOptions(eventId), enabled: false });

// Selectors for optimized component subscriptions
export const useEventById = (eventId: number) => useCachedEventDetails(eventId).data?.event ?? initialEvent;
export const useEventLoading = (eventId: number) => useCachedEventDetails(eventId).isPending;
export const useEventError = (eventId: number) => useCachedEventDetails(eventId).error?.message ?? '';
export const useEventTemplate = (eventId: number) => useCachedEventDetails(eventId).data?.template ?? INITIAL_TEMPLATE;
export const useEventCollaborators = (eventId: number) =>
    useCachedEventDetails(eventId).data?.collaborators ?? EMPTY_COLLABORATORS;
export const useEventSuggestedVenues = (eventId: number) =>
    useEventStore(state => state.events[eventId]?.suggestedVenues ?? EMPTY_VENUES);
export const useEventVenueSearchParams = (eventId: number) =>
    useEventStore(state => state.events[eventId]?.venueSearchParams);

// Hook that provides the same interface as the original useEventCollaborators context
export const useEventCollaboratorsActions = (eventId: number) => {
    const collaborators = useEventCollaborators(eventId);

    return React.useMemo(
        () => ({
            collaborators,
            removeCollaborator: (collaboratorId: number) => eventActions.removeCollaborator(eventId, collaboratorId),
            updateCollaboratorPermission: (collaboratorId: number, edit: boolean) =>
                eventActions.updateCollaboratorPermission(eventId, collaboratorId, edit),
            addCollaborators: (newCollaborators: BizlyAPI.NewEventCollaborator[]) =>
                eventActions.addCollaborators(eventId, newCollaborators),
            isAddCollaboratorsPartialError,
            isEditor: (user: Bizly.User) => eventActions.isEditor(eventId, user),
            eventId,
        }),
        [collaborators, eventId]
    );
};

// Hook that automatically gets eventId from URL params (for backward compatibility)
export const useEvent = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const numericEventId = eventId ? Number(eventId) : 0;

    React.useEffect(() => {
        if (numericEventId > 0) {
            eventActions.setActiveEvent(numericEventId);
            // Note: Loading is triggered by EventStoreInitializer / useEventManager
        }
    }, [numericEventId]);

    return useEventContext(numericEventId);
};

// For explicit eventId usage - returns the same shape as the original useEvent hook
export const useEventContext = (eventId: number) => {
    const { data, isPending, error } = useCachedEventDetails(eventId);
    const ui = useEventStore(state => state.events[eventId]);
    const event = data?.event ?? initialEvent;

    return {
        loading: isPending,
        event,
        template: data?.template ?? INITIAL_TEMPLATE,
        error: error?.message ?? '',
        suggestedVenues: ui?.suggestedVenues ?? EMPTY_VENUES,
        canEditEvent: event.editable,
        onEventChange: (newEvent: Partial<Bizly.Event>) => eventActions.onEventChange(eventId, newEvent),
        refreshEvent: () => eventActions.refreshEvent(eventId),
        onDescriptionChange: (newValue: string) => eventActions.onDescriptionChange(eventId, newValue),
        onObjectiveChange: (newValue: string) => eventActions.onObjectiveChange(eventId, newValue),
        onAddDay: (options: { append?: boolean }) => eventActions.onAddDay(eventId, options),
        onRemoveDay: (dayIndex: number) => eventActions.onRemoveDay(eventId, dayIndex),
        onDayChange: (dayIndex: number, newValue: Bizly.Day) => eventActions.onDayChange(eventId, dayIndex, newValue),
        updateSchedule: (schedule: Bizly.Day[]) => eventActions.updateSchedule(eventId, schedule),
        onVenueSelect: (venue: Bizly.Venue) => eventActions.onVenueSelect(eventId, venue),
        onLocationChange: (location: string, googlePlaceId: string) =>
            eventActions.onLocationChange(eventId, location, googlePlaceId),
        updateAttendeesCount: (attendeeCounts: Bizly.AttendeeCounts) =>
            eventActions.updateAttendeesCount(eventId, attendeeCounts),
        setEvent: (event: Bizly.Event) => eventActions.setEvent(eventId, event),
        setAcceptedProposalCurrency: (currency?: Bizly.Currency | null) =>
            eventActions.setAcceptedProposalCurrency(eventId, currency),
        reloadProvider: () => eventActions.reloadProvider(eventId),
        venueSearchParams: ui?.venueSearchParams,
    };
};

export default useEventStore;
